import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Globe, Users, Shield, Zap } from 'lucide-react';
import { fetchBurstModes, createBurstMode, updateBurstMode, deleteBurstMode } from '../services/burstModesApi';
import { fetchTeams } from '../services/teamsApi';
import { usePermissions } from '../contexts/PermissionsContext';

const BurstModeSettings = ({ models, currentUser }) => {
  const [burstModes, setBurstModes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    models: [],
    isGlobal: false,
    teamIds: []
  });
  const [errors, setErrors] = useState({});

  const { canManageBurstModes, isAdmin } = usePermissions();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [burstModesData, teamsData] = await Promise.all([
        fetchBurstModes(),
        fetchTeams()
      ]);
      setBurstModes(burstModesData);
      setTeams(teamsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (formData.models.length === 0) {
      newErrors.models = 'Select at least one model';
    }
    if (!formData.isGlobal && formData.teamIds.length === 0) {
      newErrors.teams = 'Select at least one team or make it global';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      const newBurstMode = await createBurstMode(formData);
      setBurstModes([...burstModes, newBurstMode]);
      setCreatingNew(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create burst mode:', error);
      alert('Failed to create burst mode: ' + error.message);
    }
  };

  const handleUpdate = async (id) => {
    if (!validateForm()) return;

    try {
      const updatedBurstMode = await updateBurstMode(id, formData);
      setBurstModes(burstModes.map(bm => 
        bm.id === id ? updatedBurstMode : bm
      ));
      setEditingId(null);
      resetForm();
    } catch (error) {
      console.error('Failed to update burst mode:', error);
      alert('Failed to update burst mode: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this burst mode?')) return;

    try {
      await deleteBurstMode(id);
      setBurstModes(burstModes.filter(bm => bm.id !== id));
    } catch (error) {
      console.error('Failed to delete burst mode:', error);
      alert('Failed to delete burst mode: ' + error.message);
    }
  };

  const startEdit = (burstMode) => {
    setEditingId(burstMode.id);
    setFormData({
      name: burstMode.name,
      description: burstMode.description || '',
      models: burstMode.models,
      isGlobal: burstMode.isGlobal,
      teamIds: burstMode.teamPermissions.map(tp => tp.teamId)
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      models: [],
      isGlobal: false,
      teamIds: []
    });
    setErrors({});
  };

  const toggleModel = (modelId) => {
    setFormData(prev => ({
      ...prev,
      models: prev.models.includes(modelId)
        ? prev.models.filter(id => id !== modelId)
        : [...prev.models, modelId]
    }));
  };

  const toggleTeam = (teamId) => {
    setFormData(prev => ({
      ...prev,
      teamIds: prev.teamIds.includes(teamId)
        ? prev.teamIds.filter(id => id !== teamId)
        : [...prev.teamIds, teamId]
    }));
  };

  if (loading) {
    return <div className="p-6 text-center">Loading burst modes...</div>;
  }

  if (!canManageBurstModes()) {
    return (
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-4">Burst Modes</h3>
        <p className="text-gray-600">You don't have permission to manage burst modes.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Burst Modes</h3>
        {!creatingNew && (
          <button
            onClick={() => setCreatingNew(true)}
            className="flex items-center gap-2 px-4 py-2 bg-claude-accent text-white rounded-lg hover:bg-opacity-90"
          >
            <Plus className="w-4 h-4" />
            Create Burst Mode
          </button>
        )}
      </div>

      {/* Create New Burst Mode */}
      {creatingNew && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg">
          <h4 className="font-medium mb-4">Create New Burst Mode</h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-claude-accent"
                placeholder="e.g., Creative Writing"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-claude-accent"
                rows={2}
                placeholder="Describe this burst mode..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Models <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {Object.entries(models).filter(([provider, modelList]) => Array.isArray(modelList) && modelList.length > 0).map(([provider, modelList]) => (
                  <div key={provider}>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{provider}</p>
                    {(modelList || []).map(model => (
                      <label key={model.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={formData.models.includes(model.id)}
                          onChange={() => toggleModel(model.id)}
                          className="rounded border-gray-300 text-claude-accent focus:ring-claude-accent"
                        />
                        <span className="text-sm">{model.name}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              {errors.models && <p className="text-red-500 text-xs mt-1">{errors.models}</p>}
            </div>

            <div>
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={formData.isGlobal}
                  onChange={(e) => setFormData({ ...formData, isGlobal: e.target.checked })}
                  className="rounded border-gray-300 text-claude-accent focus:ring-claude-accent"
                />
                <span className="text-sm font-medium">Make globally available</span>
                <Globe className="w-4 h-4 text-gray-500" />
              </label>
            </div>

            {!formData.isGlobal && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share with Teams
                </label>
                <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {teams.map(team => (
                    <label key={team.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={formData.teamIds.includes(team.id)}
                        onChange={() => toggleTeam(team.id)}
                        className="rounded border-gray-300 text-claude-accent focus:ring-claude-accent"
                      />
                      <span className="text-sm">{team.name}</span>
                      <span className="text-xs text-gray-500">({team.members.length} members)</span>
                    </label>
                  ))}
                </div>
                {errors.teams && <p className="text-red-500 text-xs mt-1">{errors.teams}</p>}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-claude-accent text-white rounded-lg hover:bg-opacity-90"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setCreatingNew(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Burst Modes */}
      <div className="space-y-4">
        {burstModes.map(burstMode => (
          <div key={burstMode.id} className="border border-gray-200 rounded-lg p-4">
            {editingId === burstMode.id ? (
              // Edit Mode
              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-claude-accent"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-claude-accent"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Models</label>
                  <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                    {Object.entries(models).filter(([provider, modelList]) => Array.isArray(modelList) && modelList.length > 0).map(([provider, modelList]) => (
                      <div key={provider}>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{provider}</p>
                        {(modelList || []).map(model => (
                          <label key={model.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded">
                            <input
                              type="checkbox"
                              checked={formData.models.includes(model.id)}
                              onChange={() => toggleModel(model.id)}
                              className="rounded border-gray-300 text-claude-accent focus:ring-claude-accent"
                            />
                            <span className="text-sm">{model.name}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleUpdate(burstMode.id)}
                    className="px-3 py-1.5 bg-claude-accent text-white rounded-lg hover:bg-opacity-90 text-sm"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(null);
                      resetForm();
                    }}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium flex items-center gap-2">
                      <Zap className="w-4 h-4 text-purple-600" />
                      {burstMode.name}
                      {burstMode.isGlobal && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          <Globe className="w-3 h-3" />
                          Global
                        </span>
                      )}
                    </h4>
                    {burstMode.description && (
                      <p className="text-sm text-gray-600 mt-1">{burstMode.description}</p>
                    )}
                  </div>
                  
                  {(isAdmin || burstMode.createdById === currentUser?.id) && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => startEdit(burstMode)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(burstMode.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-700 mb-1">Models ({burstMode.models.length}):</p>
                  <div className="flex flex-wrap gap-1">
                    {burstMode.models.map(modelId => {
                      const model = Object.values(models).flat().find(m => m.id === modelId);
                      return model ? (
                        <span key={modelId} className="px-2 py-0.5 bg-gray-100 text-xs rounded">
                          {model.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>

                {!burstMode.isGlobal && burstMode.teamPermissions.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-700 mb-1">
                      <Users className="w-3 h-3 inline mr-1" />
                      Shared with teams:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {burstMode.teamPermissions.map(tp => (
                        <span key={tp.teamId} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          {tp.team.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-3 text-xs text-gray-500">
                  Created by {burstMode.createdBy.name || burstMode.createdBy.email}
                </div>
              </div>
            )}
          </div>
        ))}

        {burstModes.length === 0 && !creatingNew && (
          <div className="text-center py-8 text-gray-500">
            <Zap className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No burst modes created yet</p>
            <p className="text-sm mt-1">Create your first burst mode to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BurstModeSettings;